import Decimal from 'decimal.js';
import { defaultBillingDayOfWeek } from "../../constants";
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { BillingTriggerType } from "../../types/api.model";
import { DiscountType, RecurrencePeriodTypes, ServiceCostType, ServiceStatusType } from "./model";
import * as selectors from "./selectors";
import { billingDayOfMonthModel } from '..';
import { makeManualApprovalMethod } from "../approval-method";
export function makeChanges(changes) {
  return {
    name: changes.name != null ? changes.name : null,
    description: changes.description != null ? changes.description : null,
    cost: changes.cost != null ? changes.cost : null,
    billing: changes.billing != null ? changes.billing : null
  };
}
export function makePercentDiscount(percent) {
  if (percent < 0 || percent > 100) {
    return null;
  }

  return {
    type: DiscountType.Percent,
    amount: new Decimal(percent)
  };
}
export function makeValueDiscount(value) {
  if (value < 0) {
    return null;
  }

  return {
    type: DiscountType.Value,
    amount: new Decimal(value)
  };
}
export function makeFixedCost(price, discount) {
  return {
    pricingType: ServiceCostType.Fixed,
    price: new Decimal(price),
    discount: discount || null
  };
}
export function makeVariableCost(price) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    unitCap,
    unitName,
    discount
  } = options;
  return {
    pricingType: ServiceCostType.Variable,
    price: new Decimal(price),
    unitCap: unitCap != null ? new Decimal(unitCap) : null,
    unitName: unitName || null,
    discount: discount || null
  };
}
export function makeRangeCost(maxPrice) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    minPrice,
    discount
  } = options;
  return {
    pricingType: ServiceCostType.Range,
    maxPrice: new Decimal(maxPrice),
    minPrice: minPrice != null ? new Decimal(minPrice) : null,
    discount: discount || null
  };
}
export function makeOnApprovalBilling() {
  return {
    trigger: BillingTriggerType.OnApproval
  };
}
export function makeOneTimeManualBilling() {
  return {
    trigger: BillingTriggerType.OneTimeManual
  };
}
export function makeOneTimeDeliveryBilling() {
  return {
    trigger: BillingTriggerType.OneTimeDelivery
  };
}
export function makeRepeatableManualBilling(period) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    billingDayOfMonth,
    billingDayOfWeek,
    isBilledUpfront = false,
    maxCharges
  } = options;
  return {
    trigger: BillingTriggerType.RepeatableManual,
    recurrencePeriod: period,
    isBilledUpfront,
    billingDayOfMonth: period === RecurrencePeriodTypes.Monthly ? billingDayOfMonth || billingDayOfMonthModel.makeBillingDayOfMonth() : null,
    billingDayOfWeek: selectors.periodRequiresBillingDayOfWeek(period) ? billingDayOfWeek || defaultBillingDayOfWeek : null,
    maxCharges: maxCharges != null ? maxCharges : makeMaxCharges(false)
  };
}
export function makeOngoingBilling(period) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    billingDayOfMonth,
    billingDayOfWeek,
    isPaused = false,
    isProrated = false,
    isBilledUpfront = false,
    billUpfrontState = makeBillUpfrontState(false),
    maxCharges
  } = options;
  return {
    trigger: BillingTriggerType.Ongoing,
    isPaused,
    isProrated,
    isBilledUpfront,
    billUpfrontState,
    recurrencePeriod: period,
    billingDayOfMonth: period === RecurrencePeriodTypes.Monthly ? billingDayOfMonth || billingDayOfMonthModel.makeBillingDayOfMonth() : null,
    billingDayOfWeek: selectors.periodRequiresBillingDayOfWeek(period) ? billingDayOfWeek || defaultBillingDayOfWeek : null,
    maxCharges: maxCharges != null ? maxCharges : makeMaxCharges(false)
  };
}
export function makeAwaitingApprovalStatus() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    type: ServiceStatusType.AwaitingApproval,
    awaitingApproval: {
      addedOn: options.addedOn || new Date(),
      approvalMethod: options.approvalMethod || makeManualApprovalMethod(),
      note: options.note || null
    }
  };
}
export function makeAmendedStatus() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    type: ServiceStatusType.Amended,
    date: options.date || new Date(),
    amended: {
      amendedOn: options.amendedOn || new Date(),
      changes: makeChanges(options.changes || {}),
      approvalMethod: options.approvalMethod || makeManualApprovalMethod(),
      note: options.note || null
    }
  };
}
export function makeApprovedStatus(date) {
  return {
    type: ServiceStatusType.Approved,
    date
  };
}
export function makeCompletedStatus(date, changes) {
  const status = {
    type: ServiceStatusType.Completed,
    date,
    completed: {}
  };

  if (changes) {
    status.completed.changes = makeChanges(changes);
  }

  return status;
}
export function makeTerminatedStatus(date) {
  return {
    type: ServiceStatusType.Terminated,
    date
  };
}
export function makeCanceledStatus() {
  return {
    type: ServiceStatusType.Canceled
  };
}
export function makeService(input) {
  var _input$id, _input$description, _input$status, _input$cost, _input$billing, _input$isBillable, _input$serviceTemplat, _input$participation;

  return {
    id: (_input$id = input.id) !== null && _input$id !== void 0 ? _input$id : generateUniqueId('service'),
    name: input.name,
    description: (_input$description = input.description) !== null && _input$description !== void 0 ? _input$description : '',
    status: (_input$status = input.status) !== null && _input$status !== void 0 ? _input$status : makeAwaitingApprovalStatus(),
    cost: (_input$cost = input.cost) !== null && _input$cost !== void 0 ? _input$cost : makeFixedCost(1),
    billing: (_input$billing = input.billing) !== null && _input$billing !== void 0 ? _input$billing : makeOnApprovalBilling(),
    isBillable: (_input$isBillable = input.isBillable) !== null && _input$isBillable !== void 0 ? _input$isBillable : false,
    serviceTemplateId: (_input$serviceTemplat = input.serviceTemplateId) !== null && _input$serviceTemplat !== void 0 ? _input$serviceTemplat : null,
    participation: (_input$participation = input.participation) !== null && _input$participation !== void 0 ? _input$participation : {
      type: 'mandatory'
    },
    integratedId: input.integratedId
  };
}
export function makeMaxCharges(enabled, limit) {
  return {
    enabled,
    limit: enabled ? limit || null : null
  };
}
export function makeBillUpfrontState(skipBillingOnAcceptance) {
  return {
    skipBillingOnAcceptance
  };
}
export function makeMandatoryParticipation() {
  return {
    type: 'mandatory'
  };
}
export function makeOptionalParticipation(includedByDefault) {
  return {
    type: 'optional',
    optional: {
      includedByDefault
    }
  };
}